/**
 * Procedural TitleX stabilizer viewer.
 * Reuses arrow-placeholder.glb (5mm × 25" on +X), scaled to 1/2" OD × selected length.
 * Weights, QDs, and a hunting-compound riser (GLB) with front/rear 5/16-24 bosses.
 * Grooved aluminum bar ends load from stab-bar-end.glb (CAD), not the LynX cap.
 * Front 0°/8° and rear QDs load from qd-*.glb (CAD) with procedural fallback.
 */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

export const REF_SHAFT_INCHES = 25;
export const REF_ARROW_OD_INCHES = 5 / 25.4;
export const STAB_OD_INCHES = 0.5;
export const UNIT_PER_INCH = 18.5 / 32;
export const END_CARBON_OVERLAP_IN = 0.12;
export const WRAP_AUTH_INCHES = 7;
export const WRAP_TIP_CLEAR_INCHES = 0.08;
/** Put the wrap join on the underside of the bar (mesh UV seam is authored on +Y). */
export const WRAP_SEAM_ROT_X = Math.PI;
export const REAR_CANT_DEG = 20;
/** Hand-posed rear bar (holder-local) from local pose-edit. QD stays put. */
export const REAR_BAR_POSE_IN = { x: 1.6011, y: -0.8141, z: 1.7324 };
export const REAR_BAR_POSE_DEG = { x: -0.306, y: -25.319, z: 0.152 };
export const QD_CLOSED_IN = 0.32;
export const QD_INSERT_IN = 0.12;
export const REAR_QD_SEAT_IN = 0.32;
export const REAR_C_POCKET_IN = 0.58;
export const QD_MOUTH_IN = QD_CLOSED_IN + 0.22;
const QD_GLTF_KINDS = ["0", "8", "rear"];
export const QD_PLUG_LEN_IN = 0.4;
export const QD_PLUG_OD_IN = 0.68;
export const QD_BARREL_LEN_IN = 0.95;
export const QD_BARREL_OD_IN = 0.8;
export const QD_BORE_IN = 0.32;
export const REAR_CLAMP_X_IN = 1.62;
export const REAR_CLAMP_Y_IN = -0.16;
const LOGO_LEN_IN = 5.05;
const LOGO_GAP_AFTER_CAP_IN = 0.34;
const LOGO_INSERT_PAD_IN = 0.15;
const LOGO_TIP_CLEAR_IN = 0.28;
const LOGO_MIN_LEN_IN = 2.4;
const LOGO_RADIUS_SCALE = 1.034;
const LOGO_THETA = 2.08;
const LOGO_INK = "#f7f4ec";
const LOGO_X_INK = "#c6dc3c";
const LOGO_SERIF = '"Cinzel", Georgia, "Times New Roman", serif';

const HIDE_NODES = [
  "node_vanes",
  "node_vane_1",
  "node_vane_2",
  "node_vane_3",
  "node_vane_4",
  "node_nock",
  "node_nock_lighted",
  "node_insert",
  "node_field_point",
  "node_spine_mark",
];

export const CARBON_COLOR = 0x2c2e32;
export const COAT_COLORS = {
  black: 0x2f3034,
  sand: 0xc4b48a,
  white: 0xece8e0,
  "od-green": 0x4a5c32,
};
export const BAR_COLORS = COAT_COLORS;

export const defaultViewerState = {
  front: {
    enabled: true,
    model: "micro",
    length: 12,
    color: "black",
    lynxColor: "black",
    flatsColor: "black",
    colorSplit: false,
    weightColors: [],
    included: "lynx",
    extraFlatsOz: 0,
    extraLynxOz: 0,
    qd: "0",
    endBowColor: "black",
    endTipColor: "black",
    wrapKey: "none",
    wrapUrl: null,
    wrapUv: null,
    wrapColor: null,
  },
  rear: {
    enabled: true,
    model: "micro",
    length: 10,
    color: "black",
    lynxColor: "black",
    flatsColor: "black",
    colorSplit: false,
    weightColors: [],
    included: "lynx",
    extraFlatsOz: 0,
    extraLynxOz: 0,
    qd: "rear",
    endBowColor: "black",
    endTipColor: "black",
    wrapKey: "none",
    wrapUrl: null,
    wrapUv: null,
    wrapColor: null,
  },
};

function u(inches) {
  return inches * UNIT_PER_INCH;
}

function cloneBar(THREE, src) {
  const clone = src.clone(true);
  clone.traverse((obj) => {
    if (obj.isMesh && obj.material) {
      obj.material = Array.isArray(obj.material)
        ? obj.material.map((m) => m.clone())
        : obj.material.clone();
    }
  });
  return clone;
}

function findNamed(root, name) {
  let found = null;
  root.traverse((obj) => {
    if (!found && obj.name === name) found = obj;
  });
  return found;
}

function hideArrowExtras(root) {
  root.traverse((obj) => {
    if (HIDE_NODES.indexOf(obj.name) >= 0) obj.visible = false;
  });
}

function eachMaterial(obj, fn) {
  if (!obj || !obj.isMesh || !obj.material) return;
  const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
  mats.forEach(fn);
}

function setShaftCarbon(THREE, root) {
  const shaft = findNamed(root, "node_shaft");
  if (!shaft) return;
  shaft.traverse((obj) => {
    eachMaterial(obj, (mat) => {
      mat.color.setHex(CARBON_COLOR);
      mat.map = null;
      mat.metalness = 0.05;
      mat.roughness = 0.58;
      mat.envMapIntensity = 0.2;
      mat.needsUpdate = true;
    });
  });
}

function dedicatedWrapFile(url) {
  const s = String(url || "");
  // Theme stab-wrap assets (local or CDN) are authored for the shaft UV — never
  // atlas-slice or auto-rotate them (panoramic wraps must keep landscape).
  return /(?:^|\/)stab-wrap-[^/?#]+\.png(?:$|\?)/i.test(s);
}

function wrapCacheKey(url, wrapUv) {
  if (dedicatedWrapFile(url)) return `${url}#dedicated`;
  const count = wrapUv && Number(wrapUv.count) > 1 ? Number(wrapUv.count) : 1;
  const index = wrapUv && Number.isFinite(Number(wrapUv.index)) ? Number(wrapUv.index) : 0;
  const axis = wrapUv && wrapUv.axis === "y" ? "y" : "x";
  return `${url}#${axis}:${index}/${count}`;
}

function trimWrapPadding(ctx, width, height) {
  let data;
  try {
    data = ctx.getImageData(0, 0, width, height).data;
  } catch {
    return null;
  }
  const px = (x, y) => {
    const i = (y * width + x) * 4;
    return [data[i], data[i + 1], data[i + 2], data[i + 3]];
  };
  const corners = [px(0, 0), px(width - 1, 0), px(0, height - 1), px(width - 1, height - 1)];
  const avg = [0, 0, 0];
  corners.forEach((c) => {
    avg[0] += c[0];
    avg[1] += c[1];
    avg[2] += c[2];
  });
  avg[0] /= 4;
  avg[1] /= 4;
  avg[2] /= 4;
  const step = Math.max(1, Math.floor(Math.min(width, height) / 256));
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 24) continue;
      const d = Math.hypot(data[i] - avg[0], data[i + 1] - avg[1], data[i + 2] - avg[2]);
      if (d < 30) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX <= minX || maxY <= minY) return null;
  const pad = Math.round(Math.min(width, height) * 0.01);
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;
  const area = (cw * ch) / (width * height);
  if (area < 0.12 || area > 0.97) return null;
  return { x: minX, y: minY, w: cw, h: ch };
}

function prepareWrapTexture(THREE, image, wrapUv, sourceUrl, maxAnisotropy) {
  const dedicated = dedicatedWrapFile(sourceUrl);
  const aniso = Math.max(1, Number(maxAnisotropy) || 8);

  // Theme stab-wrap PNGs are already full-bleed for node_wrap — skip canvas
  // resample (that softens detail on the thin bar).
  if (dedicated && image) {
    const mapped = new THREE.Texture(image);
    mapped.colorSpace = THREE.SRGBColorSpace;
    mapped.wrapS = THREE.RepeatWrapping;
    mapped.wrapT = THREE.ClampToEdgeWrapping;
    mapped.repeat.set(1, 1);
    mapped.offset.set(0, 0);
    mapped.anisotropy = aniso;
    mapped.minFilter = THREE.LinearMipmapLinearFilter;
    mapped.magFilter = THREE.LinearFilter;
    mapped.generateMipmaps = true;
    mapped.needsUpdate = true;
    return mapped;
  }

  const count = wrapUv && Number(wrapUv.count) > 1 ? Number(wrapUv.count) : 1;
  const index = wrapUv && Number.isFinite(Number(wrapUv.index)) ? Number(wrapUv.index) : 0;
  const axis = wrapUv && wrapUv.axis === "y" ? "y" : "x";
  const iw = image.width;
  const ih = image.height;
  let sx = 0;
  let sy = 0;
  let sw = iw;
  let sh = ih;
  if (count > 1) {
    if (axis === "y") {
      sh = ih / count;
      sy = index * sh;
    } else {
      sw = iw / count;
      sx = index * sw;
    }
  }

  const scratch = document.createElement("canvas");
  scratch.width = Math.max(1, Math.round(sw));
  scratch.height = Math.max(1, Math.round(sh));
  const scratchCtx = scratch.getContext("2d");
  scratchCtx.drawImage(image, sx, sy, sw, sh, 0, 0, scratch.width, scratch.height);
  const trimmed = trimWrapPadding(scratchCtx, scratch.width, scratch.height);
  if (trimmed) {
    sx = trimmed.x;
    sy = trimmed.y;
    sw = trimmed.w;
    sh = trimmed.h;
  } else {
    sx = 0;
    sy = 0;
    sw = scratch.width;
    sh = scratch.height;
  }

  // Pack-sheet / CDN banners may be landscape strips that need a 90° spin.
  const landscape = sw > sh * 1.08;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (landscape) {
    canvas.width = Math.max(1, Math.round(sh));
    canvas.height = Math.max(1, Math.round(sw));
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(scratch, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh);
  } else {
    canvas.width = Math.max(1, Math.round(sw));
    canvas.height = Math.max(1, Math.round(sh));
    ctx.drawImage(scratch, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  }

  const mapped = new THREE.CanvasTexture(canvas);
  mapped.colorSpace = THREE.SRGBColorSpace;
  mapped.wrapS = THREE.RepeatWrapping;
  mapped.wrapT = THREE.ClampToEdgeWrapping;
  mapped.repeat.set(1, 1);
  mapped.offset.set(0, 0);
  mapped.anisotropy = aniso;
  mapped.needsUpdate = true;
  return mapped;
}

function knockoutToLightInk(image, sx, sy, sw, sh) {
  const cut = document.createElement("canvas");
  cut.width = Math.max(1, Math.round(sw));
  cut.height = Math.max(1, Math.round(sh));
  const ctx = cut.getContext("2d");
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, cut.width, cut.height);
  let data;
  try {
    data = ctx.getImageData(0, 0, cut.width, cut.height);
  } catch {
    return null;
  }
  const px = data.data;
  const w = cut.width;
  const h = cut.height;
  const hardWhite = 242;
  const softWhite = 205;
  const softSpan = hardWhite - softWhite;
  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;
  for (let i = 0; i < px.length; i += 4) {
    const luma = 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
    let a = px[i + 3] / 255;
    if (luma > hardWhite) a = 0;
    else if (luma > softWhite) a *= (hardWhite - luma) / softSpan;
    px[i] = 255 - px[i];
    px[i + 1] = 255 - px[i + 1];
    px[i + 2] = 255 - px[i + 2];
    px[i + 3] = Math.round(Math.max(0, Math.min(1, a)) * 255);
    if (px[i + 3] < 22) continue;
    const p = i / 4;
    const x = p % w;
    const y = (p / w) | 0;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (maxX <= minX || maxY <= minY) return null;
  ctx.putImageData(data, 0, 0);
  const pad = 2;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad);
  maxY = Math.min(h - 1, maxY + pad);
  const out = document.createElement("canvas");
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out.getContext("2d").drawImage(cut, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

function extractLogoDeer(image) {
  if (!image || !image.width) return null;
  const sx = image.width * 0.7;
  return knockoutToLightInk(image, sx, 0, image.width - sx, image.height * 0.92);
}

function paintLogoMark(ctx, text, x, y) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-Math.PI / 2);
  ctx.scale(-1, -1);
  ctx.strokeText(text, 0, 0);
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function setLogoType(ctx, fill, size, strokeAlpha, lineWidth) {
  ctx.fillStyle = fill;
  ctx.strokeStyle = "rgba(20,20,22," + strokeAlpha + ")";
  ctx.lineWidth = lineWidth;
  ctx.font = "700 " + size + "px " + LOGO_SERIF;
}

function drawStackedWord(ctx, word, x, y0, size, gap) {
  for (let i = 0; i < word.length; i += 1) {
    paintLogoMark(ctx, word[i], x, y0 + i * (size + gap));
  }
}

function prepareLogoTexture(THREE, image) {
  const deer = extractLogoDeer(image);
  const w = 900;
  const h = 2200;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const cx = w * 0.44;
  const orySize = 310;
  const xSize = 470;
  const microSize = 92;
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  setLogoType(ctx, LOGO_INK, orySize, 0.35, 10);
  drawStackedWord(ctx, "ORY", cx, 240, orySize, 8);
  setLogoType(ctx, LOGO_X_INK, xSize, 0.28, 12);
  paintLogoMark(ctx, "X", cx, 1280);
  setLogoType(ctx, LOGO_INK, microSize, 0.35, 5);
  drawStackedWord(ctx, "MICRO", cx + 268, 1040, microSize, 6);
  if (deer) {
    const dw = w * 0.7;
    const dh = Math.min(dw * (deer.height / Math.max(1, deer.width)), 520);
    ctx.save();
    ctx.translate((w - dw) / 2 - 36 + dw / 2, 1620 + dh / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.scale(-1, -1);
    ctx.drawImage(deer, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function applyWrap(THREE, root, texture) {
  const wrap = findNamed(root, "node_wrap");
  if (!wrap) return;
  if (!texture) {
    wrap.visible = false;
    return;
  }
  wrap.visible = true;
  wrap.traverse((obj) => {
    eachMaterial(obj, (mat) => {
      mat.map = texture;
      mat.color.setHex(0xffffff);
      mat.metalness = 0.08;
      mat.roughness = 0.42;
      mat.envMapIntensity = 0.55;
      mat.transparent = false;
      mat.opacity = 1;
      mat.needsUpdate = true;
    });
  });
}

function layoutShaftAndWrap(root, lengthIn) {
  const shaft = findNamed(root, "node_shaft");
  const wrap = findNamed(root, "node_wrap");
  const len = Math.max(8, Number(lengthIn) || 12);
  const axial = len / REF_SHAFT_INCHES;
  const radial = STAB_OD_INCHES / REF_ARROW_OD_INCHES;
  if (shaft) {
    shaft.scale.set(axial, 1, 1);
  }
  root.scale.set(1, radial, radial);

  const cover = Math.max(1, len - WRAP_TIP_CLEAR_INCHES);
  const wrapScaleX = cover / WRAP_AUTH_INCHES;
  const wrapCxAuth = -u(REF_SHAFT_INCHES) / 2 + u(WRAP_AUTH_INCHES) / 2 + u(0.05);
  const wantedCenter = -u(WRAP_TIP_CLEAR_INCHES) / 2;
  if (wrap) {
    wrap.scale.set(wrapScaleX, 1.06, 1.06);
    wrap.position.x = wantedCenter - wrapCxAuth * wrapScaleX;
    wrap.rotation.x = WRAP_SEAM_ROT_X;
  }
}

function discMesh(THREE, radiusIn, thickIn, color, metal, rough) {
  const geo = new THREE.CylinderGeometry(u(radiusIn), u(radiusIn), u(thickIn), 36);
  geo.rotateZ(Math.PI / 2);
  const mat = new THREE.MeshStandardMaterial({
    color,
    metalness: metal,
    roughness: rough,
    envMapIntensity: 0.4,
  });
  return new THREE.Mesh(geo, mat);
}

function coatKey(bar, kind) {
  if (bar && bar.model === "pro") return "black";
  if (kind === "lynx") return (bar && (bar.lynxColor || bar.color)) || "black";
  if (kind === "flats") return (bar && (bar.flatsColor || bar.color)) || "black";
  return (bar && bar.color) || "black";
}

/** Bar→tip stack: flats, then LynX discs, then 1oz cap when LynX is present. */
export function stackPieces(bar) {
  const included = bar && bar.included === "flats" ? "flats" : "lynx";
  const extraFlats = Math.max(0, Number(bar && bar.extraFlatsOz) || 0);
  const extraLynx = Math.max(0, Number(bar && bar.extraLynxOz) || 0);
  const flatsOz = (included === "flats" ? 3 : 0) + extraFlats;
  const lynxOz = (included === "lynx" ? 2 : 0) + extraLynx;
  const pieces = [];
  let remainingFlats = flatsOz;
  let includedFlatsLeft = included === "flats" ? 3 : 0;
  while (includedFlatsLeft > 0 && remainingFlats >= 1) {
    pieces.push({ kind: "flat", oz: 1 });
    includedFlatsLeft -= 1;
    remainingFlats -= 1;
  }
  while (remainingFlats >= 2) {
    pieces.push({ kind: "flat", oz: 2 });
    remainingFlats -= 2;
  }
  while (remainingFlats >= 1) {
    pieces.push({ kind: "flat", oz: 1 });
    remainingFlats -= 1;
  }
  for (let i = 0; i < lynxOz; i++) pieces.push({ kind: "lynx", oz: 1 });
  if (lynxOz > 0) pieces.push({ kind: "cap", oz: 1 });
  return pieces;
}

function pieceCoatKey(bar, index, piece) {
  if (bar && bar.model === "pro") return "black";
  const colors = bar && Array.isArray(bar.weightColors) ? bar.weightColors : [];
  if (colors[index]) return colors[index];
  if (piece && piece.kind === "flat") return coatKey(bar, "flats");
  if (piece && (piece.kind === "lynx" || piece.kind === "cap")) return coatKey(bar, "lynx");
  return (bar && bar.color) || "black";
}

function pieceCoatHex(bar, index, piece) {
  return COAT_COLORS[pieceCoatKey(bar, index, piece)] || COAT_COLORS.black;
}

function buildStack(THREE, bar) {
  const group = new THREE.Group();
  group.name = "weight_stack";
  const pieces = stackPieces(bar);
  const lastFlat = pieces.reduce(function (acc, piece, i) {
    return piece.kind === "flat" ? i : acc;
  }, -1);
  const lastLynx = pieces.reduce(function (acc, piece, i) {
    return piece.kind === "lynx" ? i : acc;
  }, -1);
  let x = 0;

  pieces.forEach(function (piece, i) {
    const hex = pieceCoatHex(bar, i, piece);
    if (piece.kind === "flat") {
      const thick = 0.28 * piece.oz;
      const mesh = discMesh(THREE, 1.25 / 2, thick, hex, 0.42, 0.34);
      mesh.position.x = x + u(thick) / 2;
      group.add(mesh);
      x += u(thick);
      if (i !== lastFlat) x += u(0.02);
    } else if (piece.kind === "lynx") {
      const thick = 0.38;
      const mesh = discMesh(THREE, 0.75 / 2, thick, hex, 0.52, 0.28);
      mesh.position.x = x + u(thick) / 2;
      group.add(mesh);
      x += u(thick);
      if (i !== lastLynx) x += u(0.02);
    } else {
      const thick = 0.55;
      const mesh = discMesh(THREE, 0.75 / 2, thick, hex, 0.48, 0.3);
      mesh.position.x = x + u(thick) / 2;
      group.add(mesh);
      x += u(thick);
    }
  });
  return group;
}

function qdMetal(THREE, color, metal, rough) {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: metal,
    roughness: rough,
    envMapIntensity: 0.45,
  });
}

const QD_SLOT_THETA = Math.PI * 1.42;
const FRONT_CLAMP_ORIGIN = { x: QD_PLUG_LEN_IN, y: 0, z: 0 };
const REAR_CLAMP_ORIGIN = { x: REAR_CLAMP_X_IN, y: REAR_CLAMP_Y_IN, z: 0 };
const qdTex = {};

function setInchPos(obj, pos) {
  obj.position.set(u(pos.x || 0), u(pos.y || 0), u(pos.z || 0));
}

function addAxialDisc(THREE, parent, radiusIn, thickIn, color, metal, rough, xIn) {
  const mesh = discMesh(THREE, radiusIn, thickIn, color, metal, rough);
  mesh.position.x = u(xIn);
  parent.add(mesh);
  return mesh;
}

function axialSlotCyl(radiusIn, lenIn, thetaStart, thetaLen, openEnded) {
  const r = u(radiusIn);
  const geo = new THREE.CylinderGeometry(r, r, u(lenIn), 40, 1, !!openEnded, thetaStart, thetaLen);
  geo.rotateZ(Math.PI / 2);
  return geo;
}

function qdCanvasTex(THREE, w, h, paint) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, w, h);
  paint(ctx, w, h);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function qdMarkTexture(THREE, caption) {
  const key = "mark:" + (caption || "logo");
  if (qdTex[key]) return qdTex[key];
  qdTex[key] = qdCanvasTex(THREE, 1024, 256, function (ctx, w, h) {
    ctx.fillStyle = "#f3f0e6";
    ctx.strokeStyle = "#f3f0e6";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.save();
    ctx.translate(w * 0.36, h * 0.42);
    ctx.lineWidth = 7;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(-78, -44);
    ctx.lineTo(78, 44);
    ctx.moveTo(-78, 44);
    ctx.lineTo(78, -44);
    ctx.moveTo(0, -62);
    ctx.lineTo(-22, -18);
    ctx.lineTo(22, -18);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
    ctx.font = '700 36px "Cinzel", Georgia, serif';
    ctx.fillText("TITLEX ARCHERY", w * 0.36, h * 0.8);
    if (caption) {
      ctx.font = '700 78px "Cinzel", Georgia, serif';
      ctx.fillText(caption, w * 0.8, h * 0.5);
    }
  });
  return qdTex[key];
}

function qdTickTexture(THREE) {
  if (qdTex.ticks) return qdTex.ticks;
  qdTex.ticks = qdCanvasTex(THREE, 1024, 96, function (ctx, w, h) {
    ctx.strokeStyle = "#f3f0e6";
    ctx.fillStyle = "#f3f0e6";
    ctx.textAlign = "center";
    ctx.font = "600 22px sans-serif";
    for (let d = 0; d <= 8; d += 1) {
      const x = 80 + (d / 8) * (w - 160);
      const tall = d % 2 === 0;
      ctx.lineWidth = tall ? 3 : 1.5;
      ctx.beginPath();
      ctx.moveTo(x, tall ? 18 : 28);
      ctx.lineTo(x, h - 14);
      ctx.stroke();
      if (tall) ctx.fillText(String(d), x, 14);
    }
  });
  return qdTex.ticks;
}

function barSocketTiltRad(kind) {
  return kind === "8" ? THREE.MathUtils.degToRad(-8) : 0;
}

function barGroupOffset(kind) {
  if (kind === "rear") return REAR_CLAMP_ORIGIN;
  if (kind === "0" || kind === "8") return FRONT_CLAMP_ORIGIN;
  return { x: 0, y: 0, z: 0 };
}

function addQdPlug(THREE, parent) {
  const mid = QD_PLUG_LEN_IN / 2;
  addAxialDisc(THREE, parent, QD_PLUG_OD_IN / 2, QD_PLUG_LEN_IN, 0x1a1a1c, 0.8, 0.26, mid);
  addAxialDisc(THREE, parent, QD_BORE_IN / 2, QD_PLUG_LEN_IN + 0.04, 0x0c0c0e, 0.55, 0.4, mid);
  const screw = new THREE.Mesh(
    new THREE.CylinderGeometry(u(0.07), u(0.07), u(0.2), 12),
    qdMetal(THREE, 0x1a1a1c, 0.8, 0.26)
  );
  screw.position.set(u(QD_PLUG_LEN_IN * 0.55), u(QD_PLUG_OD_IN / 2 + 0.02), 0);
  parent.add(screw);
}

function addClampBarrel(THREE, parent, origin, tiltRad, caption) {
  const g = new THREE.Group();
  g.name = "qd_clamp";
  setInchPos(g, origin);
  g.rotation.z = tiltRad || 0;
  const od = QD_BARREL_OD_IN / 2;
  const closedLen = QD_CLOSED_IN;
  const slotLen = QD_BARREL_LEN_IN - closedLen;
  const thetaStart = Math.PI / 2 - QD_SLOT_THETA / 2;
  addAxialDisc(THREE, g, od, closedLen, 0x1c1c1e, 0.82, 0.25, closedLen / 2);
  const slot = new THREE.Mesh(
    axialSlotCyl(od, slotLen, thetaStart, QD_SLOT_THETA),
    qdMetal(THREE, 0x1c1c1e, 0.82, 0.25)
  );
  slot.position.x = u(closedLen + slotLen / 2);
  g.add(slot);
  const mouth = new THREE.Mesh(
    axialSlotCyl(od + 0.012, 0.06, thetaStart, QD_SLOT_THETA),
    qdMetal(THREE, 0x141416, 0.78, 0.3)
  );
  mouth.position.x = u(QD_BARREL_LEN_IN - 0.03);
  g.add(mouth);
  const decal = new THREE.Mesh(
    axialSlotCyl(od + 0.008, 0.62, thetaStart + 0.2, QD_SLOT_THETA - 0.4, true),
    new THREE.MeshStandardMaterial({
      map: qdMarkTexture(THREE, caption || ""),
      transparent: true,
      depthWrite: false,
      metalness: 0.2,
      roughness: 0.45,
      alphaTest: 0.12,
    })
  );
  decal.position.x = u(closedLen + 0.28);
  decal.renderOrder = 2;
  g.add(decal);
  parent.add(g);
  return g;
}

function buildWasher(THREE) {
  const g = new THREE.Group();
  g.name = "qd_washer";
  const flange = discMesh(THREE, 0.55, 0.18, 0x1f1f1f, 0.75, 0.3);
  g.add(flange);
  return g;
}

function buildFrontQd(THREE, kind) {
  const g = new THREE.Group();
  g.name = kind === "8" ? "qd_front_8" : "qd_front_0";
  addQdPlug(THREE, g);
  addClampBarrel(THREE, g, FRONT_CLAMP_ORIGIN, barSocketTiltRad(kind), kind === "8" ? "8°" : "0°");
  return g;
}

function rearArmGeometry() {
  const len = 1.48;
  const wid = 0.52;
  const rad = 0.2;
  const y0 = -wid / 2;
  const shape = new THREE.Shape();
  shape.moveTo(u(rad), u(y0));
  shape.lineTo(u(len - rad), u(y0));
  shape.absarc(u(len - rad), u(y0 + rad), u(rad), -Math.PI / 2, 0, false);
  shape.lineTo(u(len), u(y0 + wid - rad));
  shape.absarc(u(len - rad), u(y0 + wid - rad), u(rad), 0, Math.PI / 2, false);
  shape.lineTo(u(rad), u(y0 + wid));
  shape.absarc(u(rad), u(y0 + wid - rad), u(rad), Math.PI / 2, Math.PI, false);
  shape.lineTo(u(0), u(y0 + rad));
  shape.absarc(u(rad), u(y0 + rad), u(rad), Math.PI, Math.PI * 1.5, false);
  [0.38, 0.74, 1.1].forEach((cx) => {
    const hole = new THREE.Path();
    hole.absellipse(u(cx), 0, u(0.09), u(0.13), 0, Math.PI * 2, true);
    shape.holes.push(hole);
  });
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: u(0.42),
    bevelEnabled: true,
    bevelThickness: u(0.02),
    bevelSize: u(0.018),
    bevelSegments: 2,
    curveSegments: 10,
  });
  geo.translate(0, 0, -u(0.21));
  return geo;
}

function buildRearQd(THREE) {
  const g = new THREE.Group();
  g.name = "qd_rear";
  const mat = qdMetal(THREE, 0x1a1a1c, 0.78, 0.28);
  addAxialDisc(THREE, g, 0.36, 0.42, 0x1a1a1c, 0.78, 0.28, 0.21);
  addAxialDisc(THREE, g, QD_BORE_IN / 2, 0.5, 0x0b0b0d, 0.5, 0.42, 0.21);
  const ringGeo = new THREE.CylinderGeometry(u(0.4), u(0.4), u(0.16), 48, 1, true);
  ringGeo.rotateZ(Math.PI / 2);
  const ring = new THREE.Mesh(
    ringGeo,
    new THREE.MeshStandardMaterial({
      map: qdTickTexture(THREE),
      metalness: 0.35,
      roughness: 0.4,
      transparent: true,
      alphaTest: 0.08,
    })
  );
  ring.position.x = u(0.34);
  g.add(ring);
  const arm = new THREE.Mesh(rearArmGeometry(), mat);
  arm.position.set(u(0.28), u(-0.04), 0);
  g.add(arm);
  const knob = new THREE.Mesh(new THREE.CylinderGeometry(u(0.14), u(0.14), u(0.38), 20), mat);
  knob.position.set(u(0.38), u(-0.42), u(0.18));
  g.add(knob);
  const knobCap = discMesh(THREE, 0.16, 0.1, 0x2a2a2c, 0.7, 0.32);
  knobCap.position.set(u(0.38), u(-0.42), u(0.38));
  knobCap.rotation.y = Math.PI / 2;
  g.add(knobCap);
  addClampBarrel(THREE, g, REAR_CLAMP_ORIGIN);
  return g;
}

function buildQd(THREE, kind) {
  if (!kind || kind === "none") return buildWasher(THREE);
  if (kind === "rear") return buildRearQd(THREE);
  return buildFrontQd(THREE, kind);
}

function qdInsertInches(kind) {
  if (!kind || kind === "none") return 0;
  return QD_INSERT_IN;
}

/** +X of the clamp mouth in barGroup space, where carbon becomes visible. */
function qdMouthInches(kind) {
  if (!kind || kind === "none") return 0.09;
  return QD_MOUTH_IN;
}

function qdKind(kind) {
  return QD_GLTF_KINDS.includes(kind) ? kind : "";
}

function paintEndCoat(root, bar, which) {
  const key =
    which === "bow"
      ? (bar && (bar.endBowColor || bar.endColor || bar.color)) || "black"
      : (bar && (bar.endTipColor || bar.endColor || bar.color)) || "black";
  const hex = COAT_COLORS[key] || COAT_COLORS.black;
  root.traverse((obj) => {
    eachMaterial(obj, (mat) => {
      mat.color.setHex(hex);
      mat.metalness = 0.48;
      mat.roughness = 0.3;
      mat.envMapIntensity = 0.45;
      mat.needsUpdate = true;
    });
  });
}

function measureLengthX(root) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty()) return 0;
  return box.max.x - box.min.x;
}

function carbonFaceRadius(root) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty()) return 0;
  const x1 = box.min.x + (box.max.x - box.min.x) * 0.12;
  let maxR = 0;
  root.traverse((obj) => {
    if (!obj.isMesh || !obj.geometry) return;
    const pos = obj.geometry.getAttribute("position");
    if (!pos) return;
    const e = obj.matrixWorld.elements;
    for (let i = 0; i < pos.count; i++) {
      const lx = pos.getX(i);
      const ly = pos.getY(i);
      const lz = pos.getZ(i);
      const x = e[0] * lx + e[4] * ly + e[8] * lz + e[12];
      if (x < box.min.x - 1e-6 || x > x1) continue;
      const y = e[1] * lx + e[5] * ly + e[9] * lz + e[13];
      const z = e[2] * lx + e[6] * ly + e[10] * lz + e[14];
      const r = Math.hypot(y, z);
      if (r > maxR) maxR = r;
    }
  });
  return maxR;
}

function fitBarEndToShaft(root) {
  const carbonR = carbonFaceRadius(root);
  const shaftR = u(STAB_OD_INCHES) / 2;
  if (carbonR < 1e-6 || shaftR < 1e-6) return;
  const s = shaftR / carbonR;
  if (Math.abs(s - 1) < 0.01) return;
  root.scale.multiplyScalar(s);
  root.updateMatrixWorld(true);
}

function buildRiser(THREE) {
  const geo = new THREE.BoxGeometry(u(1.6), u(3.4), u(1.15));
  const mat = new THREE.MeshStandardMaterial({
    color: 0x2a2c30,
    metalness: 0.08,
    roughness: 0.42,
    envMapIntensity: 0.35,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "riser_stub";
  return mesh;
}

function collectRiserMounts(root) {
  return {
    front: findNamed(root, "mount_front"),
    rear: findNamed(root, "mount_rear"),
  };
}

async function tryLoadGlb(loader, src, label) {
  if (!src) return null;
  try {
    return await loader.loadAsync(src);
  } catch (err) {
    console.warn("[StabilizerViewer] " + label + " GLB failed", err);
    return null;
  }
}

function paintQdMetal(root) {
  root.traverse((obj) => {
    eachMaterial(obj, (mat) => {
      mat.color.setHex(0x1a1a1c);
      mat.metalness = 0.82;
      mat.roughness = 0.28;
      mat.envMapIntensity = 0.45;
      mat.needsUpdate = true;
    });
  });
}

function localPose(root, obj) {
  root.updateMatrixWorld(true);
  obj.updateWorldMatrix(true, false);
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  obj.getWorldPosition(pos);
  root.worldToLocal(pos);
  obj.getWorldQuaternion(quat);
  const rootQ = new THREE.Quaternion();
  root.getWorldQuaternion(rootQ);
  quat.premultiply(rootQ.invert());
  const euler = new THREE.Euler().setFromQuaternion(quat, "XYZ");
  return { pos, euler };
}

function measureBoreInches(root, clampPos, mouthPos) {
  const axis = new THREE.Vector3().subVectors(mouthPos, clampPos);
  const len = axis.length();
  if (len < 1e-6) return STAB_OD_INCHES / 2;
  axis.normalize();
  const rads = [];
  const v = new THREE.Vector3();
  const rel = new THREE.Vector3();
  root.updateMatrixWorld(true);
  root.traverse((obj) => {
    if (!obj.isMesh || !obj.geometry) return;
    const pos = obj.geometry.getAttribute("position");
    if (!pos) return;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(obj.matrixWorld);
      root.worldToLocal(v);
      rel.subVectors(v, clampPos);
      const t = rel.dot(axis);
      if (t < len * 0.15 || t > len * 0.85) continue;
      rel.addScaledVector(axis, -t);
      const r = rel.length();
      if (r > u(0.18) && r < u(0.4)) rads.push(r);
    }
  });
  if (!rads.length) return STAB_OD_INCHES / 2;
  rads.sort((a, b) => a - b);
  return rads[Math.floor(rads.length * 0.2)] / UNIT_PER_INCH;
}

function captureQdMetrics(root) {
  const clamp = findNamed(root, "qd_clamp");
  const mouth = findNamed(root, "qd_mouth");
  const pose = clamp ? localPose(root, clamp) : null;
  const offset = pose
    ? {
        x: pose.pos.x / UNIT_PER_INCH,
        y: pose.pos.y / UNIT_PER_INCH,
        z: pose.pos.z / UNIT_PER_INCH,
      }
    : { x: 0, y: 0, z: 0 };
  let mouthIn = QD_MOUTH_IN;
  let mouthPos = null;
  if (mouth && pose) {
    mouthPos = localPose(root, mouth).pos;
    mouthIn = mouthPos.distanceTo(pose.pos) / UNIT_PER_INCH;
  }
  root.userData.qdMetrics = {
    insert: QD_INSERT_IN,
    mouth: mouthIn,
    bore: mouthPos && pose ? measureBoreInches(root, pose.pos, mouthPos) : STAB_OD_INCHES / 2,
    offset,
    rotY: pose ? pose.euler.y : 0,
    rotZ: pose ? pose.euler.z : 0,
  };
  return root.userData.qdMetrics;
}

export function createStabilizerViewer(hostEl, options = {}) {
  if (!hostEl) throw new Error("createStabilizerViewer requires a host element");

  const modelSrc = options.modelSrc || "/models/arrow-placeholder.glb";
  const endGlbSrc = options.endGlbSrc || "";
  const riserGlbSrc = options.riserGlbSrc || "";
  const logoSrc = options.logoSrc || "";
  const qdGlbSrc = options.qdGlbSrc || {};
  const wrapTextureBaseUrl = String(options.wrapTextureBaseUrl || "").replace(/\/$/, "");
  const wrapAssetV = String(options.wrapAssetV || "").trim();

  function withWrapCache(url) {
    if (!url || !wrapAssetV) return url;
    const join = url.includes("?") ? "&" : "?";
    return `${url}${join}v=${encodeURIComponent(wrapAssetV)}`;
  }
  let state = {
    front: { ...defaultViewerState.front, ...(options.initialState && options.initialState.front) },
    rear: { ...defaultViewerState.rear, ...(options.initialState && options.initialState.rear) },
  };
  let destroyed = false;
  let renderer;
  let scene;
  let camera;
  let controls;
  let template = null;
  let endTemplate = null;
  let endLength = 0;
  let logoTexture = null;
  const qdTemplates = { 0: null, 8: null, rear: null };
  const texCache = new Map();
  const bars = { front: null, rear: null };
  let riser = null;
  let riserMounts = { front: null, rear: null };
  let grid = null;
  let raf = 0;
  let poseEditApi = null;

  hostEl.innerHTML = "";
  hostEl.classList.add("titlex-stabilizer-viewer");
  hostEl.style.position = "relative";
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-label", "TitleX custom stabilizer preview");
  hostEl.appendChild(canvas);
  const status = document.createElement("p");
  status.className = "stabilizer-playground__hint";
  status.style.position = "absolute";
  status.style.left = "1rem";
  status.style.bottom = "1rem";
  status.textContent = "Loading 3D preview…";
  hostEl.appendChild(status);

  function resolveWrapUrl(bar) {
    if (!bar || !bar.wrapKey || bar.wrapKey === "none") return null;
    if (bar.wrapUrl && /^https?:\/\//i.test(bar.wrapUrl)) return withWrapCache(bar.wrapUrl);
    if (bar.wrapUrl && wrapTextureBaseUrl) {
      const file = String(bar.wrapUrl).replace(/^.*\//, "");
      return withWrapCache(`${wrapTextureBaseUrl}/${file}`);
    }
    if (wrapTextureBaseUrl) {
      return withWrapCache(`${wrapTextureBaseUrl}/stab-wrap-${bar.wrapKey}.png`);
    }
    return withWrapCache(bar.wrapUrl || null);
  }

  async function loadTexture(url, wrapUv) {
    if (!url) return null;
    const key = wrapCacheKey(url, wrapUv);
    if (texCache.has(key)) return texCache.get(key);
    const maxAniso =
      renderer && renderer.capabilities
        ? renderer.capabilities.getMaxAnisotropy()
        : 16;
    const pending = new Promise((resolve) => {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin("anonymous");
      loader.load(
        url,
        (tex) => {
          try {
            resolve(prepareWrapTexture(THREE, tex.image, wrapUv, url, maxAniso));
          } catch {
            resolve(null);
          }
        },
        undefined,
        () => resolve(null)
      );
    });
    texCache.set(key, pending);
    return pending;
  }

  async function loadLogoTexture(url) {
    if (!url) return null;
    if (document.fonts && document.fonts.load) {
      try {
        await document.fonts.load("700 200px Cinzel");
      } catch {}
    }
    return new Promise((resolve) => {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin("anonymous");
      loader.load(
        url,
        (tex) => {
          try {
            resolve(prepareLogoTexture(THREE, tex.image));
          } catch {
            resolve(null);
          }
        },
        undefined,
        () => resolve(null)
      );
    });
  }

  function barParts(holder) {
    return holder.userData.barGroup || holder;
  }

  function qdMetrics(kind) {
    const template = qdTemplates[qdKind(kind)];
    return template?.userData?.qdMetrics ?? null;
  }

  function visibleCapInches() {
    return endLength > 0 ? endLength / UNIT_PER_INCH - END_CARBON_OVERLAP_IN : 0;
  }

  function insertInches(kind) {
    if (qdKind(kind) === "rear") return Math.max(0, visibleCapInches());
    return qdMetrics(kind)?.insert ?? qdInsertInches(kind);
  }

  function mouthInches(kind) {
    return qdMetrics(kind)?.mouth ?? qdMouthInches(kind);
  }

  function attachLogo(holder, slot, barState) {
    if (!logoTexture || !holder || !barState || !barState.enabled) return;
    if ((barState.wrapKey && barState.wrapKey !== "none") || barState.wrapColor) return;
    const insert = insertInches(barState.qd);
    const mouth = qdKind(barState.qd) === "rear" ? 0 : mouthInches(barState.qd);
    const capIn = visibleCapInches();
    const carbonEnd = insert + (Number(barState.length) || 12);
    const carbonStart = Math.max(insert + LOGO_INSERT_PAD_IN, mouth + capIn + LOGO_GAP_AFTER_CAP_IN);
    const carbonFinish = carbonEnd - capIn - LOGO_TIP_CLEAR_IN;
    const maxLen = carbonFinish - carbonStart;
    if (maxLen < LOGO_MIN_LEN_IN) return;
    const lenIn = Math.min(LOGO_LEN_IN, maxLen);
    const start = carbonStart + Math.max(0, maxLen - lenIn) / 2;
    const radius = (u(STAB_OD_INCHES) / 2) * LOGO_RADIUS_SCALE;
    const geo = new THREE.CylinderGeometry(radius, radius, u(lenIn), 32, 1, true, -LOGO_THETA / 2, LOGO_THETA);
    geo.rotateZ(Math.PI / 2);
    const mesh = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({
        map: logoTexture,
        color: 0xffffff,
        transparent: true,
        depthWrite: false,
        metalness: 0.06,
        roughness: 0.4,
        envMapIntensity: 0.18,
        emissive: 0x2a2a2a,
        alphaTest: 0.06,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      })
    );
    mesh.name = "bar_logo";
    mesh.renderOrder = 3;
    mesh.position.x = u(start + lenIn / 2);
    if (slot === "rear") mesh.rotation.y = Math.PI;
    holder.userData.logoMesh = mesh;
    barParts(holder).add(mesh);
  }

  function poseBarGroup(holder, kind) {
    const parts = holder && holder.userData.barGroup;
    if (!parts) return;
    if (qdKind(kind) === "rear") {
      setInchPos(parts, REAR_BAR_POSE_IN);
      parts.rotation.set(
        THREE.MathUtils.degToRad(REAR_BAR_POSE_DEG.x),
        THREE.MathUtils.degToRad(REAR_BAR_POSE_DEG.y),
        THREE.MathUtils.degToRad(REAR_BAR_POSE_DEG.z)
      );
      parts.scale.set(1, 1, 1);
      return;
    }
    const qd = holder.userData.qdMesh;
    const clamp = qd && findNamed(qd, "qd_clamp");
    const mouth = qd && findNamed(qd, "qd_mouth");
    if (clamp && mouth) {
      holder.updateWorldMatrix(true, false);
      clamp.updateWorldMatrix(true, false);
      mouth.updateWorldMatrix(true, false);
      const cPos = holder.worldToLocal(clamp.getWorldPosition(new THREE.Vector3()));
      const mPos = holder.worldToLocal(mouth.getWorldPosition(new THREE.Vector3()));
      const dir = new THREE.Vector3().subVectors(mPos, cPos);
      const q = new THREE.Quaternion();
      if (dir.lengthSq() > 1e-10) {
        q.setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir.normalize());
      }
      parts.position.copy(cPos);
      const tilt = localPose(qd, clamp).euler.z;
      if (tilt) {
        q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), tilt));
      }
      parts.quaternion.copy(q);
      parts.scale.set(1, 1, 1);
      return;
    }
    const m = qdMetrics(kind);
    if (m) {
      setInchPos(parts, m.offset);
      parts.rotation.set(0, m.rotY || 0, m.rotZ || 0);
      return;
    }
    setInchPos(parts, barGroupOffset(kind));
    parts.rotation.set(0, 0, barSocketTiltRad(kind));
  }

  function clearGroup(group) {
    if (!group) return;
    ["stack", "qdMesh", "endMount", "endTip", "logoMesh", "rearPlug"].forEach((key) => {
      const obj = group.userData[key];
      if (obj && obj.parent) obj.parent.remove(obj);
      group.userData[key] = null;
    });
  }

  function attachBarEnds(holder, barState, insert) {
    if (!endTemplate || endLength <= 0) return 0;
    const parts = barParts(holder);
    const overlap = u(END_CARBON_OVERLAP_IN);
    const mount = cloneBar(THREE, endTemplate);
    mount.name = "end_mount";
    paintEndCoat(mount, barState, "bow");
    mount.rotation.y = Math.PI;
    if (qdKind(barState.qd) === "rear") {
      mount.position.x = endLength;
    } else {
      mount.position.x = u(mouthInches(barState.qd)) + endLength - overlap;
    }
    holder.userData.endMount = mount;
    parts.add(mount);

    const tip = cloneBar(THREE, endTemplate);
    tip.name = "end_tip";
    paintEndCoat(tip, barState, "tip");
    tip.position.x = u(insert + barState.length) - overlap;
    holder.userData.endTip = tip;
    parts.add(tip);
    return endLength - overlap;
  }

  async function layoutBar(slot, barState) {
    const holder = bars[slot];
    if (!holder) return;
    holder.visible = !!barState.enabled;
    if (!barState.enabled) return;

    const parts = barParts(holder);

    const clone = holder.userData.arrow;
    layoutShaftAndWrap(clone, barState.length);
    setShaftCarbon(THREE, clone);
    const tex = await loadTexture(resolveWrapUrl(barState), barState.wrapUv);
    applyWrap(THREE, clone, tex);

    const insert = insertInches(barState.qd);
    clone.position.x = u(barState.length / 2 + insert);

    clearGroup(holder);
    const stackShift = attachBarEnds(holder, barState, insert);
    const stack = buildStack(THREE, barState);
    stack.userData.procedural = true;
    stack.position.x = u(barState.length + insert) + stackShift;
    holder.userData.stack = stack;
    parts.add(stack);

    const template = qdTemplates[qdKind(barState.qd)];
    const qd = template ? cloneBar(THREE, template) : buildQd(THREE, barState.qd);
    if (template) paintQdMetal(qd);
    else qd.userData.procedural = true;
    qd.name = qd.name || slot + "_qd";
    holder.userData.qdMesh = qd;
    holder.add(qd);
    poseBarGroup(holder, barState.qd);
    attachLogo(holder, slot, barState);
    if (poseEditApi) poseEditApi.refresh();
  }

  function snapBarToMount(holder, mount) {
    if (!holder || !mount) return false;
    mount.updateWorldMatrix(true, false);
    holder.position.setFromMatrixPosition(mount.matrixWorld);
    holder.quaternion.setFromRotationMatrix(mount.matrixWorld);
    return true;
  }

  function placeBars() {
    if (!snapBarToMount(bars.front, riserMounts.front) && bars.front) {
      bars.front.position.set(u(0.8), 0, 0);
      bars.front.rotation.set(0, 0, 0);
    }
    if (!snapBarToMount(bars.rear, riserMounts.rear) && bars.rear) {
      const stubCant =
        state.rear && state.rear.qd === "rear" && !riserMounts.rear
          ? THREE.MathUtils.degToRad(REAR_CANT_DEG)
          : 0;
      bars.rear.position.set(-u(0.8), 0, 0);
      bars.rear.rotation.set(0, Math.PI - stubCant, 0);
    }
  }

  function placeGrid() {
    if (!grid) return;
    grid.position.y = -u(2.4);
    if (!riser) return;
    const b = new THREE.Box3().setFromObject(riser);
    if (!b.isEmpty()) grid.position.y = b.min.y - u(0.35);
  }

  const LOOK_DIR = new THREE.Vector3(0.62, 0.28, 0.72).normalize();
  const LOOK_DIR_REAR = new THREE.Vector3(-0.62, 0.28, 0.72).normalize();
  const MIN_CAM_DIST = u(1.6);
  const CAM_DUR_MS = 420;
  let camTween = null;
  let spinning = false;
  let spinAfterTween = false;

  function unionBox(box, obj) {
    if (!obj) return box;
    obj.updateWorldMatrix(true, true);
    obj.traverseVisible((child) => {
      if (!child.isMesh || !child.geometry) return;
      const piece = new THREE.Box3().setFromObject(child);
      if (!piece.isEmpty()) box.union(piece);
    });
    return box;
  }

  function overviewBox() {
    const box = new THREE.Box3();
    unionBox(box, bars.front);
    unionBox(box, bars.rear);
    unionBox(box, riserMounts.front);
    unionBox(box, riserMounts.rear);
    unionBox(box, riser && findNamed(riser, "grip"));
    return box;
  }

  function partBox(focus) {
    const holder = focus && bars[focus.slot];
    const part = focus && focus.part;
    if (!holder || !holder.visible || !part || part === "overview") return overviewBox();
    const box = new THREE.Box3();
    const { qdMesh, endMount, endTip, stack } = holder.userData;
    if (part === "bar") {
      unionBox(box, holder);
    } else if (part === "qd" || part === "start") {
      unionBox(box, qdMesh);
      unionBox(box, endMount);
    } else if (part === "end" || part === "weights") {
      unionBox(box, endTip);
      unionBox(box, stack);
    }
    if (box.isEmpty()) return overviewBox();
    box.expandByScalar(part === "bar" ? u(0.7) : u(2.5));
    return box;
  }

  function applyFrame(box, animate, lookDir) {
    if (!camera || !controls) return;
    const dir = lookDir || LOOK_DIR;
    if (!box || box.isEmpty()) {
      camera.position.set(u(14), u(6), u(18));
      controls.target.set(0, 0, 0);
      controls.minDistance = MIN_CAM_DIST;
      controls.maxDistance = u(120);
      controls.update();
      return;
    }
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const aspect = Math.max(0.2, camera.aspect || 1);
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const fitH = size.y / (2 * tanHalf);
    const fitW = size.x / (2 * tanHalf * aspect);
    const dist = 1.22 * Math.max(fitH, fitW, size.z * 1.15, u(4.2));
    const dest = center.clone().addScaledVector(dir, dist);
    commitFrame(dest, center, dist, animate);
  }

  function frameCamera(animate) {
    applyFrame(overviewBox(), animate);
  }

  function resetCamera() {
    setSpinning(false);
    frameCamera(true);
  }

  function focusCamera(focus) {
    setSpinning(false);
    const dir = focus && focus.slot === "rear" ? LOOK_DIR_REAR : LOOK_DIR;
    applyFrame(partBox(focus), true, dir);
  }

  function spinTarget(fallbackBox) {
    const mid = new THREE.Vector3();
    if (riserMounts.front && riserMounts.rear) {
      const a = new THREE.Vector3();
      const b = new THREE.Vector3();
      riserMounts.front.updateWorldMatrix(true, false);
      riserMounts.rear.updateWorldMatrix(true, false);
      a.setFromMatrixPosition(riserMounts.front.matrixWorld);
      b.setFromMatrixPosition(riserMounts.rear.matrixWorld);
      return mid.copy(a).add(b).multiplyScalar(0.5);
    }
    const grip = riser && findNamed(riser, "grip");
    if (grip) return new THREE.Box3().setFromObject(grip).getCenter(mid);
    return fallbackBox.getCenter(mid);
  }

  function maxCornerRadius(box, target) {
    const corner = new THREE.Vector3();
    let radius = 0;
    for (let ix = 0; ix < 2; ix++) {
      for (let iy = 0; iy < 2; iy++) {
        for (let iz = 0; iz < 2; iz++) {
          corner.set(
            ix ? box.max.x : box.min.x,
            iy ? box.max.y : box.min.y,
            iz ? box.max.z : box.min.z
          );
          radius = Math.max(radius, corner.distanceTo(target));
        }
      }
    }
    return radius;
  }

  function spinLookDir() {
    const aspect = Math.max(0.2, camera && camera.aspect ? camera.aspect : 1);
    const dir = LOOK_DIR.clone();
    if (aspect > 1.25) {
      dir.y += 0.34;
      dir.normalize();
    }
    return dir;
  }

  function spinFitDistance(box, target, radius) {
    const aspect = Math.max(0.2, camera.aspect || 1);
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const height = box && !box.isEmpty() ? Math.max(box.max.y - box.min.y, u(3)) : radius;
    const above = box && !box.isEmpty() ? Math.max(0, box.max.y - target.y) : height * 0.5;
    const below = box && !box.isEmpty() ? Math.max(0, target.y - box.min.y) : height * 0.5;
    const fitH = Math.max(above, below, height * 0.5) / tanHalf;
    const fitW = radius / (tanHalf * aspect);
    return 1.22 * Math.max(fitH, fitW, u(5));
  }

  function frameSpin(animate) {
    if (!camera || !controls) return;
    const box = overviewBox();
    const target = spinTarget(box);
    const radius = box.isEmpty() ? u(10) : Math.max(maxCornerRadius(box, target), u(6));
    const dist = spinFitDistance(box, target, radius);
    let dir = spinLookDir();
    if (animate === false && camera) {
      const offset = camera.position.clone().sub(controls.target);
      if (offset.lengthSq() > 1e-6) {
        offset.y = Math.max(offset.y, dist * 0.18);
        dir = offset.normalize();
      }
    }
    commitFrame(target.clone().addScaledVector(dir, dist), target, dist, animate);
  }

  function commitFrame(dest, target, dist, animate) {
    camera.near = Math.max(0.04, dist / 80);
    camera.far = Math.max(400, dist * 24);
    camera.updateProjectionMatrix();
    controls.minDistance = MIN_CAM_DIST;
    controls.maxDistance = Math.max(u(100), dist * 8);
    if (!animate) {
      camTween = null;
      camera.position.copy(dest);
      controls.target.copy(target);
      controls.update();
      return;
    }
    camTween = {
      fromPos: camera.position.clone(),
      fromTarget: controls.target.clone(),
      toPos: dest,
      toTarget: target,
      t0: performance.now(),
    };
  }

  function setSpinning(on) {
    spinning = !!on;
    spinAfterTween = false;
    if (!controls) return spinning;
    if (!spinning) {
      controls.autoRotate = false;
      return spinning;
    }
    controls.autoRotate = false;
    frameSpin(true);
    spinAfterTween = true;
    return spinning;
  }

  function toggleSpin() {
    return setSpinning(!spinning);
  }

  function resize() {
    if (!renderer || !camera || destroyed) return;
    const w = hostEl.clientWidth || 640;
    const h = hostEl.clientHeight || 420;
    renderer.setSize(w, h, false);
    camera.aspect = w / Math.max(1, h);
    camera.updateProjectionMatrix();
    if (spinning && !camTween) frameSpin(false);
  }

  function tick() {
    if (destroyed) return;
    raf = requestAnimationFrame(tick);
    if (camTween && camera && controls) {
      const t = Math.min(1, (performance.now() - camTween.t0) / CAM_DUR_MS);
      const e = 1 - Math.pow(1 - t, 3);
      camera.position.lerpVectors(camTween.fromPos, camTween.toPos, e);
      controls.target.lerpVectors(camTween.fromTarget, camTween.toTarget, e);
      if (t >= 1) {
        camTween = null;
        if (spinAfterTween && spinning) {
          controls.autoRotate = true;
          spinAfterTween = false;
        }
      }
    }
    if (controls) controls.update();
    if (renderer && scene && camera) renderer.render(scene, camera);
  }

  async function applyState() {
    if (!template) return;
    await layoutBar("front", state.front);
    await layoutBar("rear", state.rear);
    placeBars();
    if (poseEditApi) poseEditApi.refresh();
  }

  function inchVec(v) {
    return {
      x: +(v.x / UNIT_PER_INCH).toFixed(4),
      y: +(v.y / UNIT_PER_INCH).toFixed(4),
      z: +(v.z / UNIT_PER_INCH).toFixed(4),
    };
  }

  function degEuler(e) {
    return {
      x: +THREE.MathUtils.radToDeg(e.x).toFixed(3),
      y: +THREE.MathUtils.radToDeg(e.y).toFixed(3),
      z: +THREE.MathUtils.radToDeg(e.z).toFixed(3),
    };
  }

  async function installPoseEdit() {
    if (!options.poseEdit || !scene || !camera || !canvas) return null;
    let TransformControls;
    try {
      ({ TransformControls } = await import("three/addons/controls/TransformControls.js"));
    } catch (err) {
      console.warn("[StabilizerViewer] pose edit unavailable", err);
      return null;
    }

    const xform = new TransformControls(camera, canvas);
    xform.setSize(0.85);
    xform.setSpace("local");
    xform.setMode("translate");
    scene.add(xform);
    xform.addEventListener("dragging-changed", (ev) => {
      if (controls) controls.enabled = !ev.value;
    });
    xform.addEventListener("objectChange", publishPose);

    const panel = document.createElement("div");
    panel.className = "stab-pose-edit";
    panel.innerHTML =
      '<div class="stab-pose-edit__row">' +
      "<strong>Pose edit</strong>" +
      '<button type="button" data-pose-copy>Copy</button>' +
      "</div>" +
      '<label class="stab-pose-edit__field">Object' +
      '<select data-pose-target>' +
      '<option value="">— click a part —</option>' +
      '<option value="rear:qdMesh">Rear QD</option>' +
      '<option value="rear:barGroup">Rear bar</option>' +
      '<option value="rear:endMount">Rear stabilizer end</option>' +
      '<option value="rear:holder">Rear bar + QD</option>' +
      '<option value="front:qdMesh">Front QD</option>' +
      '<option value="front:barGroup">Front bar</option>' +
      '<option value="front:endMount">Front stabilizer end</option>' +
      '<option value="front:holder">Front bar + QD</option>' +
      '<option value="riser">Riser</option>' +
      "</select></label>" +
      '<div class="stab-pose-edit__row" data-pose-modes>' +
      '<button type="button" data-pose-mode="translate">Move</button>' +
      '<button type="button" data-pose-mode="rotate">Rotate</button>' +
      '<button type="button" data-pose-space="local">Local</button>' +
      '<button type="button" data-pose-space="world">World</button>' +
      "</div>" +
      '<p class="stab-pose-edit__hint">W move · E rotate · L local/world · Esc clear · drag the arrows</p>' +
      '<pre data-pose-out>Click a part, then drag.</pre>';
    hostEl.appendChild(panel);

    const targetSel = panel.querySelector("[data-pose-target]");
    const outEl = panel.querySelector("[data-pose-out]");
    let sel = null;
    let rest = null;
    let ptr = null;

    function resolveSel(next) {
      if (!next) return null;
      if (next === "riser" || next.slot === "riser") return riser;
      const slot = typeof next === "string" ? next.split(":")[0] : next.slot;
      const key = typeof next === "string" ? next.split(":")[1] : next.key;
      if (slot === "riser") return riser;
      const holder = bars[slot];
      if (!holder) return null;
      if (key === "holder") return holder;
      return holder.userData[key] || null;
    }

    function selValue() {
      if (!sel) return "";
      if (sel === "riser" || sel.slot === "riser") return "riser";
      return sel.slot + ":" + sel.key;
    }

    function snapshot() {
      const obj = resolveSel(sel);
      if (!obj) {
        const empty = { selected: null };
        window.__stabPose = empty;
        return empty;
      }
      const world = new THREE.Euler().setFromQuaternion(obj.getWorldQuaternion(new THREE.Quaternion()), "XYZ");
      const deltaPos = rest ? obj.position.clone().sub(rest.pos) : new THREE.Vector3();
      const deltaQuat = rest ? rest.quat.clone().invert().multiply(obj.quaternion) : new THREE.Quaternion();
      const deltaEuler = new THREE.Euler().setFromQuaternion(deltaQuat, "XYZ");
      const data = {
        selected: selValue(),
        name: obj.name || "(unnamed)",
        parent: (obj.parent && obj.parent.name) || "",
        mode: xform.getMode(),
        space: xform.space,
        localInches: inchVec(obj.position),
        localDeg: degEuler(obj.rotation),
        worldInches: inchVec(obj.getWorldPosition(new THREE.Vector3())),
        worldDeg: degEuler(world),
        deltaInches: inchVec(deltaPos),
        deltaDeg: degEuler(deltaEuler),
      };
      window.__stabPose = data;
      return data;
    }

    function publishPose() {
      const data = snapshot();
      if (outEl) outEl.textContent = JSON.stringify(data, null, 2);
      if (targetSel) targetSel.value = selValue();
      panel.querySelectorAll("[data-pose-mode]").forEach((btn) => {
        btn.setAttribute("aria-pressed", btn.getAttribute("data-pose-mode") === xform.getMode() ? "true" : "false");
      });
      panel.querySelectorAll("[data-pose-space]").forEach((btn) => {
        btn.setAttribute("aria-pressed", btn.getAttribute("data-pose-space") === xform.space ? "true" : "false");
      });
    }

    function attach(next) {
      const obj = resolveSel(next);
      sel = obj ? next : null;
      if (!obj) {
        xform.detach();
        rest = null;
        publishPose();
        if (outEl && !next) outEl.textContent = "Click a part, then drag.";
        return;
      }
      rest = { pos: obj.position.clone(), quat: obj.quaternion.clone() };
      xform.attach(obj);
      publishPose();
    }

    function pickFromEvent(ev) {
      const rect = canvas.getBoundingClientRect();
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(
        new THREE.Vector2(
          ((ev.clientX - rect.left) / rect.width) * 2 - 1,
          -((ev.clientY - rect.top) / rect.height) * 2 + 1
        ),
        camera
      );
      const roots = [bars.front, bars.rear, riser].filter(Boolean);
      const hits = raycaster.intersectObjects(roots, true).filter((hit) => {
        let o = hit.object;
        while (o) {
          if (o === xform || o.parent === xform) return false;
          o = o.parent;
        }
        return hit.object.isMesh;
      });
      if (!hits.length) return;
      const obj = hits[0].object;
      for (const slot of ["front", "rear"]) {
        const holder = bars[slot];
        if (!holder) continue;
        for (const key of ["endMount", "endTip", "qdMesh", "barGroup"]) {
          const group = holder.userData[key];
          if (group && (obj === group || group.getObjectById(obj.id))) {
            attach({ slot, key });
            return;
          }
        }
        if (obj === holder || holder.getObjectById(obj.id)) {
          attach({ slot, key: "holder" });
          return;
        }
      }
      if (riser && (obj === riser || riser.getObjectById(obj.id))) attach("riser");
    }

    canvas.addEventListener("pointerdown", (ev) => {
      if (ev.button !== 0) return;
      ptr = { x: ev.clientX, y: ev.clientY, gizmo: !!xform.axis || xform.dragging };
    });
    canvas.addEventListener("pointerup", (ev) => {
      if (ev.button !== 0 || !ptr) return;
      const drag = Math.hypot(ev.clientX - ptr.x, ev.clientY - ptr.y) > 6;
      const gizmo = ptr.gizmo || xform.dragging;
      ptr = null;
      if (drag || gizmo) return;
      pickFromEvent(ev);
    });

    targetSel.addEventListener("change", () => {
      const value = targetSel.value;
      if (!value) attach(null);
      else if (value === "riser") attach("riser");
      else {
        const [slot, key] = value.split(":");
        attach({ slot, key });
      }
    });
    panel.querySelector("[data-pose-copy]").addEventListener("click", async () => {
      const text = JSON.stringify(snapshot(), null, 2);
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        window.prompt("Copy pose JSON", text);
      }
    });
    panel.addEventListener("click", (ev) => {
      const mode = ev.target && ev.target.getAttribute && ev.target.getAttribute("data-pose-mode");
      const space = ev.target && ev.target.getAttribute && ev.target.getAttribute("data-pose-space");
      if (mode) xform.setMode(mode);
      if (space) xform.setSpace(space);
      publishPose();
    });

    function onKey(ev) {
      if (ev.target && /^(INPUT|SELECT|TEXTAREA)$/.test(ev.target.tagName)) return;
      if (ev.key === "w" || ev.key === "W") xform.setMode("translate");
      else if (ev.key === "e" || ev.key === "E") xform.setMode("rotate");
      else if (ev.key === "l" || ev.key === "L") xform.setSpace(xform.space === "local" ? "world" : "local");
      else if (ev.key === "Escape") attach(null);
      else return;
      publishPose();
    }
    window.addEventListener("keydown", onKey);
    publishPose();

    return {
      snapshot,
      refresh() {
        const obj = resolveSel(sel);
        if (sel && obj) {
          rest = { pos: obj.position.clone(), quat: obj.quaternion.clone() };
          xform.attach(obj);
          publishPose();
        } else if (sel) attach(null);
      },
      dispose() {
        window.removeEventListener("keydown", onKey);
        xform.detach();
        scene.remove(xform);
        xform.dispose();
        panel.remove();
      },
    };
  }

  function setState(partial) {
    if (partial.front) state.front = { ...state.front, ...partial.front };
    if (partial.rear) state.rear = { ...state.rear, ...partial.rear };
    return applyState();
  }

  async function exportPng(filename = "titlex-stabilizer.png") {
    if (!renderer) throw new Error("Viewer not ready");
    renderer.render(scene, camera);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("PNG export failed");
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
    return blob;
  }

  function destroy() {
    destroyed = true;
    camTween = null;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", resize);
    if (poseEditApi) {
      poseEditApi.dispose();
      poseEditApi = null;
    }
    if (renderer) renderer.dispose();
    hostEl.innerHTML = "";
  }

  const ready = (async () => {
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xdde1e6);
    camera = new THREE.PerspectiveCamera(38, 1, 0.05, 400);
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.45;
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envScene = new THREE.Scene();
    envScene.background = new THREE.Color(0xf3f5f8);
    envScene.add(new THREE.HemisphereLight(0xffffff, 0x6e7680, 1.1));
    scene.environment = pmrem.fromScene(envScene, 0.04).texture;
    pmrem.dispose();

    scene.add(new THREE.HemisphereLight(0xf7f9fc, 0x7c838c, 0.95));
    scene.add(new THREE.AmbientLight(0xffffff, 0.28));
    const key = new THREE.DirectionalLight(0xffffff, 1.35);
    key.position.set(u(14), u(22), u(16));
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xd7dee6, 0.7);
    fill.position.set(u(-12), u(8), u(-10));
    scene.add(fill);
    grid = new THREE.GridHelper(u(64), 16, 0xc5ccd3, 0xd8dde3);
    grid.position.y = -u(2.4);
    scene.add(grid);

    controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 3;
    controls.minPolarAngle = 0.18;
    controls.maxPolarAngle = Math.PI / 2 - 0.08;
    controls.minDistance = MIN_CAM_DIST;
    controls.maxDistance = u(140);
    controls.addEventListener("start", function () {
      camTween = null;
      if (spinning) {
        controls.autoRotate = true;
        spinAfterTween = false;
      }
    });

    const gltfLoader = new GLTFLoader();
    const gltf = await gltfLoader.loadAsync(modelSrc);
    template = gltf.scene;
    hideArrowExtras(template);

    const endGltf = await tryLoadGlb(gltfLoader, endGlbSrc, "bar-end");
    if (endGltf) {
      endTemplate = endGltf.scene;
      fitBarEndToShaft(endTemplate);
      endLength = measureLengthX(endTemplate);
    }

    logoTexture = await loadLogoTexture(logoSrc);

    const qdLoads = [
      ["0", qdGlbSrc["0"] || qdGlbSrc[0], "qd-0"],
      ["8", qdGlbSrc["8"] || qdGlbSrc[8], "qd-8"],
      ["rear", qdGlbSrc.rear, "qd-rear"],
    ];
    for (const [key, src, label] of qdLoads) {
      const loaded = await tryLoadGlb(gltfLoader, src || "", label);
      if (!loaded) continue;
      paintQdMetal(loaded.scene);
      captureQdMetrics(loaded.scene);
      qdTemplates[key] = loaded.scene;
    }

    const riserGltf = await tryLoadGlb(gltfLoader, riserGlbSrc, "riser");
    if (riserGltf) {
      riser = riserGltf.scene;
      if (!riser.name) riser.name = "stab_riser";
      riserMounts = collectRiserMounts(riser);
    } else {
      riser = buildRiser(THREE);
    }
    scene.add(riser);
    placeGrid();

    ["front", "rear"].forEach((slot) => {
      const holder = new THREE.Group();
      holder.name = slot + "_bar";
      const parts = new THREE.Group();
      parts.name = slot + "_parts";
      const arrow = cloneBar(THREE, template);
      hideArrowExtras(arrow);
      holder.userData.barGroup = parts;
      holder.userData.arrow = arrow;
      parts.add(arrow);
      holder.add(parts);
      bars[slot] = holder;
      scene.add(holder);
    });

    await applyState();
    resize();
    frameCamera();
    window.addEventListener("resize", resize);
    if (typeof ResizeObserver !== "undefined") {
      new ResizeObserver(resize).observe(hostEl);
    }
    poseEditApi = await installPoseEdit();
    tick();
    if (status.parentNode) status.remove();
  })().catch((err) => {
    console.warn("[StabilizerViewer] failed", err);
    status.textContent = "3D preview failed: " + (err && err.message ? err.message : String(err));
    throw err;
  });

  const api = {
    setState,
    getState: () => ({ front: { ...state.front }, rear: { ...state.rear } }),
    destroy,
    resetCamera,
    focus: focusCamera,
    setSpinning,
    toggleSpin,
    isSpinning: () => spinning,
    exportPng,
    getPose: () => (poseEditApi ? poseEditApi.snapshot() : null),
    ready,
  };
  return api;
}

window.StabilizerViewer = {
  createStabilizerViewer,
  defaultViewerState,
  stackPieces,
  BAR_COLORS,
  COAT_COLORS,
  REF_SHAFT_INCHES,
  STAB_OD_INCHES,
};
window.dispatchEvent(new Event("stabilizer-viewer-ready"));
